import { definedMap } from "@glideapps/ts-necessities";

import {
    ActionPropertyDescriptor,
    ComponentBuilder,
    ComponentConfiguratorContext,
    ComponentDescriptor,
    EnumPropertyCase,
    PropertyDescriptor,
    SchemaInspector,
} from ".";
import { SerializedComponent } from "../../components/SerializedApp";
import { getDocURL } from "../../docUrl";
import { definedMapWithDefault } from "../../Support";
import { makeScreenContextForActions } from "../actions";
import { makeText } from "../builder-utils";
import {
    ActionDescription,
    ComponentDescription,
    ComponentKind,
    InputOutputTables,
    MutatingScreenKind,
    PropertyDescription,
    PropertyKind,
    TableGlideType,
} from "../description";
import { makeShowToastAction } from "../form-on-submit";
import { Appearance } from "./button-appearance";
import { makeTextPropertyDescriptor } from "./descriptor-utils";
import { ComponentHandlerBase } from "./handler";
import { Mood, reducedMoodCases } from "./mood";
import { getDefaultPrimitiveActionKinds } from "./primitive";
import { PropertySection } from "./property-sections";
import { getActionProperty, getEnumProperty, getIconProperty } from "./property-utils";

const ComponentKindButtonBar: ComponentKind = "button-bar";

interface ButtonBarComponentDescription extends ComponentDescription {
    readonly appearanceLeft: PropertyDescription;
    readonly iconLeft: PropertyDescription | undefined;
    readonly titleLeft: PropertyDescription | undefined;
    readonly actionLeft: PropertyDescription;

    readonly appearanceRight: PropertyDescription;
    readonly iconRight: PropertyDescription | undefined;
    readonly titleRight: PropertyDescription | undefined;
    readonly actionRight: PropertyDescription;

    readonly moodLeft: PropertyDescription | undefined;
    readonly moodRight: PropertyDescription | undefined;
}

function makeAppearanceCases(): readonly EnumPropertyCase<Appearance>[] {
    const cases: EnumPropertyCase<Appearance>[] = [
        {
            value: Appearance.Filled,
            label: "Filled",
            icon: "buttonFilled",
        },
        {
            value: Appearance.Transparent,
            label: "Transparent",
            icon: "buttonTransparent",
        },
        {
            value: Appearance.Bordered,
            label: "Bordered",
            icon: "buttonBordered",
        },
        {
            value: Appearance.Simple,
            label: "Simple",
            icon: "buttonSimple",
        },
    ];

    return cases;
}

function getActions(
    desc: ButtonBarComponentDescription
): { actionLeft: readonly ActionDescription[]; actionRight: readonly ActionDescription[] } {
    return {
        actionLeft: definedMap(getActionProperty(desc.actionLeft), a => [a]) ?? [],
        actionRight: definedMap(getActionProperty(desc.actionRight), a => [a]) ?? [],
    };
}

export class ButtonBarComponentHandler extends ComponentHandlerBase<ButtonBarComponentDescription> {
    constructor() {
        super(ComponentKindButtonBar);
    }

    public entryForTableColumn(
        builder: ComponentBuilder,
        desc: ButtonBarComponentDescription,
        tables: InputOutputTables,
        mutatingScreenKind: MutatingScreenKind | undefined,
        _validFlag: SerializedComponent | undefined,
        outputContext: SerializedComponent
    ): SerializedComponent {
        const { b } = builder;
        const { input } = tables;

        let propertyName: string | undefined;

        // Can we use `b.constant` here?
        const ctx = definedMapWithDefault(propertyName, b.data({}), n => b.getProperty(n, b.getContext, input));

        const { actionLeft, actionRight } = getActions(desc);

        const { screenContext, lets } = makeScreenContextForActions(
            builder,
            [...actionLeft, ...actionRight],
            tables,
            mutatingScreenKind
        );

        const onTapLeft = builder.makeSequenceFromActions(
            actionLeft,
            tables,
            screenContext,
            outputContext,
            mutatingScreenKind,
            false
        );
        if (onTapLeft === undefined) {
            builder.error("No action for button");
            return b.emptyUI;
        }

        const onTapRight = builder.makeSequenceFromActions(
            actionRight,
            tables,
            screenContext,
            outputContext,
            mutatingScreenKind,
            false
        );
        if (onTapRight === undefined) {
            builder.error("No action for button");
            return b.emptyUI;
        }

        const appearanceLeft = getEnumProperty(desc.appearanceLeft) ?? Appearance.Filled;
        const appearanceRight = getEnumProperty(desc.appearanceRight) ?? Appearance.Transparent;

        const moodLeft = getEnumProperty(desc.moodLeft) ?? Mood.Default;
        const moodRight = getEnumProperty(desc.moodRight) ?? Mood.Default;

        const ui = b.ui("ButtonBar", {
            textLeft: makeText(builder, desc.titleLeft, input),
            appearanceLeft: b.constant(appearanceLeft),
            moodLeft: b.constant(moodLeft),
            iconLeft: definedMap(getIconProperty(desc.iconLeft), b.constant),
            onTapLeft: b.bindContextToValue(onTapLeft, ctx),

            textRight: makeText(builder, desc.titleRight, input),
            appearanceRight: b.constant(appearanceRight),
            moodRight: b.constant(moodRight),
            iconRight: definedMap(getIconProperty(desc.iconRight), b.constant),
            onTapRight: b.bindContextToValue(onTapRight, ctx),
        });
        return b.let(lets, ui);
    }

    public getDescriptor(
        _desc: ButtonBarComponentDescription | undefined,
        _table: TableGlideType | undefined,
        _ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ComponentDescriptor<ButtonBarComponentDescription> {
        const properties: PropertyDescriptor[] = [
            makeTextPropertyDescriptor("titleLeft", "Title", "Enter title", true, mutatingScreenKind, {
                isCaption: undefined,
                preferredNames: ["caption", "title", "name", "description"],
                propertySection: PropertySection.LeftButton,
                searchable: false,
                columnFirst: false,
                defaultValue: "Left",
            }),

            makeTextPropertyDescriptor("titleRight", "Title", "Enter title", true, mutatingScreenKind, {
                isCaption: undefined,
                preferredNames: ["caption", "title", "name", "description"],
                propertySection: PropertySection.RightButton,
                searchable: false,
                columnFirst: false,
                defaultValue: "Right",
            }),
            {
                kind: PropertyKind.Enum,
                property: { name: "moodLeft" },
                label: "Mood",
                menuLabel: "Mood",
                cases: reducedMoodCases,
                defaultCaseValue: Mood.Default,
                section: PropertySection.LeftButton,
                visual: "dropdown",
            },
            {
                kind: PropertyKind.Enum,
                property: { name: "moodRight" },
                label: "Mood",
                menuLabel: "Mood",
                cases: reducedMoodCases,
                defaultCaseValue: Mood.Default,
                section: PropertySection.RightButton,
                visual: "dropdown",
            },
            {
                kind: PropertyKind.Icon,
                property: { name: "iconLeft" },
                label: "Icon",
                defaultIcon: undefined,
                section: PropertySection.LeftButton,
            },
            {
                kind: PropertyKind.Icon,
                property: { name: "iconRight" },
                label: "Icon",
                defaultIcon: undefined,
                section: PropertySection.RightButton,
            },
            {
                kind: PropertyKind.Enum,
                property: { name: "appearanceLeft" },
                label: "Style",
                menuLabel: "Show as",
                cases: makeAppearanceCases(),
                defaultCaseValue: Appearance.Filled,
                section: PropertySection.LeftButton,
                visual: "small-images",
            },
            {
                kind: PropertyKind.Enum,
                property: { name: "appearanceRight" },
                label: "Style",
                menuLabel: "Show as",
                cases: makeAppearanceCases(),
                defaultCaseValue: Appearance.Transparent,
                section: PropertySection.RightButton,
                visual: "small-images",
            },

            ...this.getBasePropertyDescriptors(),
        ];

        return {
            name: "Button Bar",
            description: "Tap to perform an action",
            img: "componentButtonBar",
            group: "Buttons",
            helpUrl: getDocURL("buttonBar"),
            properties,
        };
    }

    public getActionDescriptors(
        _desc: ButtonBarComponentDescription | undefined,
        _table: TableGlideType | undefined,
        schema: SchemaInspector | undefined,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): readonly ActionPropertyDescriptor<ButtonBarComponentDescription>[] {
        const actionKinds = getDefaultPrimitiveActionKinds(schema, mutatingScreenKind);
        return [
            {
                kind: PropertyKind.Action,
                property: { name: "actionLeft" },
                label: "Action",
                required: true,
                kinds: actionKinds,
                defaultAction: () => makeShowToastAction("Left"),
                section: PropertySection.LeftAction,
            },
            {
                kind: PropertyKind.Action,
                property: { name: "actionRight" },
                label: "Action",
                required: true,
                kinds: actionKinds,
                defaultAction: () => makeShowToastAction("Right"),
                section: PropertySection.RightAction,
            },
        ];
    }
}
